import React from "react";
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton, List, ListItemButton, ListItemText, Menu, MenuItem, Paper, Switch, TextField, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { enqueueSnackbar } from "notistack";
import globals from "../../Globals";
import { FormDialog, SelectField } from "../../Layout/Common";
import PagedTable from "../../Layout/PagedTable";
import { FIXED_TABLE, TIERS, tierOf, buildFolderTree, countBelow, usd4 } from "./loc";

const mono = { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 };
const langsKey = () => `loc.langs.${globals.selectedTitle}`;

// Where a folder's texts go (the backend's LocBundle): the app's per-language download, the build's boot subset, or
// nowhere - an owner system (the News feed) serves them on demand
const BUNDLES = [
    { value: "default", label: "Default bundle (the per-language file the app downloads)" },
    { value: "boot", label: "Boot subset (shipped in the build: loading screen, boot popups)" },
    { value: "onDemand", label: "On demand (in no file; its owner system serves the texts)" },
];
const serviceTitle = (id) => (globals.loginData?.titles?.rolesDefinition?.services ?? []).find(s => s.id === id)?.title ?? id;

// Strings: the folder tree on the left, the tags of the selected folder on the right with the base text, the note
// and the chosen languages as editable cells (tier chip and outdated badge per cell), the review proposals, and the
// tag/folder operations. Every write goes through the backend's rules (LocalizationEdit).
export default class StringsTab extends React.Component {
    state = {
        folder: null, tags: [], loadingTags: false, expanded: {},
        langs: [], editing: null, editText: "", editSpecialized: false, saving: false,
        menu: null,                 // { anchor, tag }
        dialog: null,               // { kind: create|folderEdit|folderNew|rename|folderDelete|tagDelete|move|review|reviewEdit|bulk, ... }
        reviews: null,              // the title's pending proposals ({ count, items }) - the counter, the badges, the pending view
        pendingMode: false,         // the right side lists every pending proposal instead of a folder
        busy: null,                 // a review action in flight
    };

    componentDidMount() {
        const { project, initialFolder, initialTag, tree } = this.props;
        let langs = [];
        try { langs = JSON.parse(localStorage.getItem(langsKey()) || "[]"); } catch { }
        const codes = project.languages.map(l => l.code).filter(c => c !== project.baseLanguage);
        langs = langs.filter(l => codes.includes(l));
        if (langs.length === 0 && codes.length) langs = [codes[0]];
        let folder = initialFolder || (initialTag ? initialTag.slice(0, initialTag.lastIndexOf("/")) : null);
        if (!folder || !tree.folders.some(f => f.path === folder)) folder = tree.folders.slice().sort((a, b) => a.order - b.order).find(f => f.tags > 0)?.path ?? tree.folders[0]?.path ?? null;
        const expanded = {};
        if (folder) { const parts = folder.split("/"); for (let i = 1; i < parts.length; i++) expanded[parts.slice(0, i).join("/")] = true; }
        this.setState({ langs, folder, expanded }, () => this.reload());
        this.loadReviews();
    }

    // ---------- the English review's proposals ----------
    loadReviews = () => this.props.loc("Loading suggestions", ["reviews"], { silent: true })
        .then(r => { if (r && typeof r !== "string") this.setState({ reviews: r }); return true; })
        .catch(() => true);

    // pending proposals per folder, counted up the tree (a parent shows its subfolders' too)
    pendingByFolder() {
        const map = {};
        (this.state.reviews?.items ?? []).forEach(i => {
            const parts = (i.folder ?? "").split("/");
            for (let k = 1; k <= parts.length; k++) { const f = parts.slice(0, k).join("/"); map[f] = (map[f] || 0) + 1; }
        });
        return map;
    }

    applyReview = (accept, paths, all = false, text = undefined) => {
        this.setState({ busy: all ? "*" : paths[0], dialog: null });
        const payload = all ? { all: true } : text !== undefined ? { paths, text } : { paths };
        this.props.loc(accept ? "Accepting" : "Rejecting", ["review", accept ? "accept" : "reject"], { payload, silent: true, retry: false, timeout: 120000 })
            .then(r => {
                if (typeof r === "string") { enqueueSnackbar(r, { variant: "error" }); return; }
                if (r.failed?.length) enqueueSnackbar(`${r.failed.length} could not be applied: ${r.failed[0].reason}`, { variant: "warning" });
                else enqueueSnackbar(accept ? `${r.accepted} accepted (translations marked outdated)` : `${r.rejectedProposals} rejected`, { variant: "success" });
                this.loadReviews(); this.reload(); this.props.reloadTree();
            })
            .catch(e => enqueueSnackbar(e.message, { variant: "error" }))
            .finally(() => this.setState({ busy: null }));
    }

    reload = () => {
        const { folder } = this.state;
        this.loadReviews();
        if (!folder) { this.setState({ tags: [] }); return Promise.resolve(); }
        this.setState({ loadingTags: true });
        return this.props.loc("Loading tags", ["tags", folder], { silent: true })
            .then(r => { if (r && typeof r !== "string" && r.folder === this.state.folder) this.setState({ tags: r.tags ?? [] }); })
            .catch(() => {})
            .finally(() => this.setState({ loadingTags: false }));
    }

    selectFolder = (path) => {
        this.setState({ folder: path, tags: [], editing: null, expanded: { ...this.state.expanded, [path]: true } }, () => { this.reload(); this.props.onFolderChange(path); });
    }
    toggle = (path) => this.setState(s => ({ expanded: { ...s.expanded, [path]: !s.expanded[path] } }));

    toggleLang = (code) => {
        const langs = this.state.langs.includes(code) ? this.state.langs.filter(l => l !== code) : [...this.state.langs, code];
        try { localStorage.setItem(langsKey(), JSON.stringify(langs)); } catch { }
        this.setState({ langs, editing: null });
    }

    replaceTag = (tag) => this.setState(s => ({ tags: s.tags.map(t => t.path === tag.path ? tag : t) }));

    // ---------- cells ----------
    startEdit = (tag, lang) => {
        if (!this.props.canEdit || this.state.saving) return;
        const cell = tag.texts?.[lang];
        this.setState({ editing: { path: tag.path, lang }, editText: cell?.text ?? "", editSpecialized: (cell?.tier ?? 0) >= 5 });
    }
    startNote = (tag) => { if (this.props.canEdit && !this.state.saving) this.setState({ editing: { path: tag.path, lang: "$note" }, editText: tag.note ?? "" }); }
    cancelEdit = () => this.setState({ editing: null });

    saveEdit = () => {
        const { editing, editText, editSpecialized } = this.state;
        const { project, loc } = this.props;
        if (!editing) return;
        const isNote = editing.lang === "$note";
        const payload = isNote ? { note: editText } : {
            texts: { [editing.lang]: editing.lang !== project.baseLanguage && editText === "" ? null : editText },
            tier: editSpecialized ? "HumanSpecialized" : "HumanRevised",
        };
        this.setState({ saving: true });
        loc("Saving", ["tag", "update", editing.path], { payload, silent: true })
            .then(r => {
                if (typeof r === "string") { enqueueSnackbar(r, { variant: "error" }); return; }
                if (r?.tag) this.replaceTag(r.tag);
                if (r?.baseChanged && r.outdated?.length) enqueueSnackbar(`Base text changed: ${r.outdated.join(", ")} marked outdated`, { variant: "info" });
                this.setState({ editing: null });
            })
            .catch(e => enqueueSnackbar(e.message, { variant: "error" }))
            .finally(() => this.setState({ saving: false }));
    }

    onEditKey = (e) => {
        if (e.key === "Escape") this.cancelEdit();
        else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) this.saveEdit();
    }

    // ---------- tag operations ----------
    translateNow = (tag) => {
        this.props.loc("Translating", ["translate", "isolated", tag.path], { silent: true, retry: false, timeout: 90000 })
            .then(r => {
                if (typeof r === "string") { enqueueSnackbar(r, { variant: "error" }); return; }
                enqueueSnackbar(r.status === "done" ? `${tag.name}: ${r.summary}` : `${tag.name}: translation ${r.status}`, { variant: r.status === "done" ? "success" : "info" });
                this.props.onJobsChanged();
                this.reload();
            }).catch(e => enqueueSnackbar(e.message, { variant: "error" }));
    }

    createTag = () => {
        const { dialog } = this.state;
        const payload = { path: (dialog.path ?? "").trim(), note: dialog.note ?? "", texts: { [this.props.project.baseLanguage]: dialog.text ?? "" }, autoTranslate: dialog.autoTranslate !== false };
        this.setState({ saving: true });
        this.props.loc("Creating the tag", ["tag", "create"], { payload, silent: true, retry: false, timeout: 90000 })
            .then(r => {
                if (typeof r === "string") { this.setState(s => ({ dialog: { ...s.dialog, error: r } })); return; }
                const job = r.job;
                enqueueSnackbar(job ? `Created; ${job.status === "done" ? job.summary : "translation " + job.status}` : r.jobError ? `Created; translation not started: ${r.jobError}` : "Created", { variant: "success" });
                this.setState({ dialog: null });
                const folder = payload.path.slice(0, payload.path.lastIndexOf("/"));
                this.props.reloadTree();
                this.props.onJobsChanged();
                if (folder !== this.state.folder) this.selectFolder(folder); else this.reload();
            })
            .catch(e => this.setState(s => ({ dialog: { ...s.dialog, error: e.message } })))
            .finally(() => this.setState({ saving: false }));
    }

    runDialog = () => {
        const { dialog, folder } = this.state;
        const { loc, reloadTree } = this.props;
        const done = (r, after) => {
            if (typeof r === "string") { this.setState(s => ({ dialog: { ...s.dialog, error: r } })); return; }
            this.setState({ dialog: null });
            after(r);
        };
        const fail = (e) => this.setState(s => ({ dialog: { ...s.dialog, error: e.message } }));
        switch (dialog.kind) {
            case "create": return this.createTag();
            case "tagDelete": return loc("Deleting the tag", ["tag", "delete", dialog.tag.path], { silent: true }).then(r => done(r, () => { reloadTree(); this.reload(); })).catch(fail);
            case "move": return loc("Moving the tag", ["tag", "move", dialog.tag.path, (dialog.newPath ?? "").trim()], { silent: true }).then(r => done(r, () => { reloadTree(); this.reload(); })).catch(fail);
            case "folderEdit": return loc("Saving the folder", ["folder", "set", folder], { payload: { description: dialog.description ?? "", bundle: dialog.bundle ?? "default" }, silent: true }).then(r => done(r, () => reloadTree())).catch(fail);
            case "folderNew": return loc("Creating the folder", ["folder", "create"], { payload: { path: (dialog.path ?? "").trim(), description: dialog.description ?? "" }, silent: true }).then(r => done(r, () => { reloadTree(); this.selectFolder(r.path); })).catch(fail);
            case "rename": return loc("Renaming the folder", ["folder", "rename", folder, (dialog.newPath ?? "").trim()], { silent: true }).then(r => done(r, () => { reloadTree(); this.selectFolder(r.path); })).catch(fail);
            case "folderDelete": return loc("Deleting the folder", ["folder", "delete", folder], { silent: true }).then(r => done(r, () => { reloadTree(); const i = folder.lastIndexOf("/"); this.selectFolder(i > 0 ? folder.slice(0, i) : (this.props.tree.folders[0]?.path ?? null)); })).catch(fail);
            case "review": {
                const proposal = dialog.tag.review?.text ?? "";
                const text = (dialog.text ?? proposal);
                return this.applyReview(dialog.accept, [dialog.tag.path], false, dialog.accept && text !== proposal ? text : undefined);
            }
            case "reviewEdit": return this.applyReview(true, [dialog.path], false, dialog.text ?? "");
            case "bulk": return this.applyReview(dialog.accept, dialog.paths ?? [], !!dialog.all);
            default: return null;
        }
    }

    // ---------- render ----------
    renderTree() {
        const { tree, canEdit } = this.props;
        const { folder, expanded } = this.state;
        const { roots } = buildFolderTree(tree.folders);
        const pending = this.pendingByFolder();
        const rows = [];
        const walk = (node, depth) => {
            const name = node.path.slice(node.path.lastIndexOf("/") + 1);
            const open = !!expanded[node.path];
            rows.push(
                <ListItemButton key={node.path} dense selected={node.path === folder} onClick={() => this.selectFolder(node.path)} sx={{ pl: 1 + depth * 1.5, py: 0.25 }}>
                    <Box sx={{ width: 22 }} onClick={e => { e.stopPropagation(); this.toggle(node.path); }}>
                        {node.children.length > 0 && (open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />)}
                    </Box>
                    <ListItemText primary={name} primaryTypographyProps={{ variant: "body2", noWrap: true }} />
                    {pending[node.path] > 0 && <Tooltip title={`${pending[node.path]} suggestion(s) pending here or below`}><Chip size="small" color="secondary" label={pending[node.path]} sx={{ height: 16, fontSize: 10, mr: 0.5 }} /></Tooltip>}
                    {node.bundle === "boot" && <Tooltip title="in the boot subset (shipped in the build)"><Box sx={{ width: 8, height: 8, borderRadius: 4, bgcolor: "success.main", mr: 0.5 }} /></Tooltip>}
                    {node.bundle === "onDemand" && <Tooltip title={`on demand: ${node.owner ? serviceTitle(node.owner) + " serves these texts" : "in no file"}`}><Box sx={{ width: 8, height: 8, borderRadius: 4, bgcolor: "info.main", mr: 0.5 }} /></Tooltip>}
                    <Typography variant="caption" color="text.secondary">{node.tags}{node.children.length ? `/${countBelow(node)}` : ""}</Typography>
                </ListItemButton>
            );
            if (open) node.children.forEach(c => walk(c, depth + 1));
        };
        roots.forEach(r => walk(r, 0));
        return (
            <Paper sx={{ width: 300, flexShrink: 0, maxHeight: "72vh", overflow: "auto" }}>
                <Box sx={{ display: "flex", alignItems: "center", px: 1, pt: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ flex: 1 }}>Folders</Typography>
                    {canEdit && <Button size="small" startIcon={<AddIcon />} onClick={() => this.setState({ dialog: { kind: "folderNew", path: folder ? folder + "/" : "" } })}>Folder</Button>}
                </Box>
                <List dense disablePadding>{rows}</List>
            </Paper>
        );
    }

    renderCell(tag, lang) {
        const { project, canEdit } = this.props;
        const { editing, editText, editSpecialized, saving } = this.state;
        const cell = tag.texts?.[lang];
        const isBase = lang === project.baseLanguage;
        if (editing && editing.path === tag.path && editing.lang === lang) {
            return (
                <Box sx={{ minWidth: 220 }}>
                    <TextField value={editText} onChange={e => this.setState({ editText: e.target.value })} onKeyDown={this.onEditKey} size="small" fullWidth multiline autoFocus minRows={1} maxRows={8} />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                        {!isBase && <FormControlLabel sx={{ mr: 0 }} control={<Checkbox size="small" checked={editSpecialized} onChange={e => this.setState({ editSpecialized: e.target.checked })} />} label={<Typography variant="caption">specialized</Typography>} />}
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" onClick={this.cancelEdit} disabled={saving}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={this.saveEdit} disabled={saving}>Save</Button>
                    </Box>
                </Box>
            );
        }
        const tier = tierOf(cell?.tier);
        return (
            <Box onClick={() => this.startEdit(tag, lang)} sx={{ cursor: canEdit ? "text" : "default", minWidth: 160, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                <Typography variant="body2" component="div">{cell?.text ? cell.text : <Typography component="span" variant="body2" color="text.disabled">—</Typography>}</Typography>
                <Box sx={{ display: "flex", gap: 0.5, mt: 0.25, alignItems: "center" }}>
                    <Tooltip title={`${tier.label}${cell?.updatedBy ? ` · ${cell.updatedBy}` : ""}`}><Chip size="small" label={tier.short} sx={{ bgcolor: tier.color, height: 18, fontSize: 10 }} /></Tooltip>
                    {cell?.outdated && <Tooltip title="the base text changed after this translation"><Chip size="small" color="warning" label="outdated" sx={{ height: 18, fontSize: 10 }} /></Tooltip>}
                </Box>
            </Box>
        );
    }

    // "Check with AI" on an edit popup: sends the current text, the proposal and what the person typed (a text or an
    // instruction such as "make it shorter"); the answer stays in the popup until the person applies or saves
    checkWithAi = (path, input) => {
        this.setState(s => ({ dialog: { ...s.dialog, checking: true, error: null } }));
        this.props.loc("Checking with the AI", ["review", "check", path], { payload: { text: input }, silent: true, retry: false, timeout: 90000 })
            .then(r => {
                if (typeof r === "string") { this.setState(s => ({ dialog: { ...s.dialog, checking: false, error: r } })); return; }
                this.setState(s => (s.dialog ? { dialog: { ...s.dialog, checking: false, ai: r } } : null));
            })
            .catch(e => this.setState(s => ({ dialog: { ...s.dialog, checking: false, error: e.message } })));
    }

    renderAiCheck(dialog, path, input, set) {
        const ai = dialog.ai;
        return (
            <Box sx={{ mt: 1 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button size="small" variant="outlined" onClick={() => this.checkWithAi(path, input)} disabled={!!dialog.checking || !(input ?? "").trim()}
                            startIcon={dialog.checking ? <CircularProgress size={14} /> : null}>{dialog.checking ? "Asking…" : "Check with AI"}</Button>
                    <Typography variant="caption" color="text.secondary">Type a text, or an instruction such as "make it shorter". The AI answers a text and its opinion; nothing is saved until you press the save button.</Typography>
                </Box>
                {ai && (
                    <Paper variant="outlined" sx={{ p: 1, mt: 1, bgcolor: "action.hover" }}>
                        <Typography variant="body2">{ai.note}</Typography>
                        <Typography sx={{ whiteSpace: "pre-wrap", fontWeight: 500, my: 0.5 }}>{ai.text}</Typography>
                        {ai.warning && <Typography variant="caption" color="warning.main" sx={{ display: "block" }}>{ai.warning}</Typography>}
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <Button size="small" onClick={() => set({ text: ai.text })} disabled={ai.text === input}>Use this text</Button>
                            <Typography variant="caption" color="text.secondary">{ai.unchanged ? "the AI kept your text as it is" : "the AI's text differs from what you typed"} · {ai.model} · ${(ai.usd ?? 0).toFixed(4)}</Typography>
                        </Box>
                    </Paper>
                )}
            </Box>
        );
    }

    renderDialog() {
        const { dialog, saving, folder } = this.state;
        const { project } = this.props;
        if (!dialog) return null;
        const set = (patch) => this.setState(s => ({ dialog: { ...s.dialog, ...patch, error: null } }));
        const err = dialog.error && <Typography color="error" variant="body2">{dialog.error}</Typography>;
        const field = (label, key, extra = {}) => <TextField label={label} value={dialog[key] ?? ""} onChange={e => set({ [key]: e.target.value })} size="small" fullWidth sx={{ mt: 2 }} {...extra} />;
        const props = { open: true, onClose: () => this.setState({ dialog: null }), onConfirm: this.runDialog, confirmDisabled: saving, fullWidth: true, maxWidth: "sm" };
        switch (dialog.kind) {
            case "create": return (
                <FormDialog {...props} title="New tag" confirmText="Create">
                    {err}
                    {field("Path", "path", { helperText: "folder/name, ASCII letters, digits, _ - .", inputProps: { style: mono } })}
                    {field(`${project.baseLanguage} text`, "text", { multiline: true, minRows: 2 })}
                    {field("Note", "note", { helperText: "for translators and the AI: what {0} means, where it shows, how long it may be" })}
                    <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={dialog.autoTranslate !== false} onChange={e => set({ autoTranslate: e.target.checked })} />} label="Translate into every language now (AI isolated)" />
                </FormDialog>);
            case "tagDelete": return <FormDialog {...props} title="Delete tag" confirmText="Delete">{err}<Typography>Delete “{dialog.tag.path}” in every language?</Typography></FormDialog>;
            case "move": return <FormDialog {...props} title="Move tag" confirmText="Move">{err}{field("New path", "newPath", { inputProps: { style: mono } })}</FormDialog>;
            case "folderEdit": return (
                <FormDialog {...props} title={`Folder “${folder}”`} confirmText="Save">
                    {err}
                    {field("Description", "description", { multiline: true, minRows: 2, helperText: "where these texts show; the AI reads it as context" })}
                    <SelectField label="Bundle" value={dialog.bundle ?? "default"} options={BUNDLES} onChange={v => set({ bundle: v })} fullWidth size="small" sx={{ mt: 2 }} disabled={!!dialog.owner} />
                    {dialog.owner && <Typography variant="caption" color="text.secondary">The folder belongs to {serviceTitle(dialog.owner)}, which keeps it on demand and manages its tags; the texts are edited here or there.</Typography>}
                </FormDialog>);
            case "folderNew": return <FormDialog {...props} title="New folder" confirmText="Create">{err}{field("Path", "path", { inputProps: { style: mono } })}{field("Description", "description")}</FormDialog>;
            case "rename": return <FormDialog {...props} title={`Rename “${folder}”`} confirmText="Rename">{err}{field("New path", "newPath", { inputProps: { style: mono }, helperText: "every tag and subfolder moves with it" })}</FormDialog>;
            case "folderDelete": return <FormDialog {...props} title="Delete folder" confirmText="Delete">{err}<Typography>Delete the empty folder “{folder}”?</Typography></FormDialog>;
            case "bulk": return (
                <FormDialog {...props} title={dialog.label} confirmText={dialog.accept ? "Accept" : "Reject"}>
                    {err}
                    <Typography>{dialog.accept
                        ? `Write ${dialog.paths.length} proposed ${project.baseLanguage} texts (human specialized) and mark their translations outdated? The next context run redoes those translations.`
                        : "Drop every pending proposal? A new English review can propose them again."}</Typography>
                </FormDialog>);
            case "review": {
                const r = dialog.tag.review;
                const base = dialog.tag.texts?.[project.baseLanguage]?.text ?? "";
                const text = dialog.text ?? r.text;
                const edited = text !== r.text;
                return (
                    <FormDialog open title={`Suggestion for “${dialog.tag.path}”`} onClose={props.onClose} onConfirm={() => { set({ accept: true }); setTimeout(this.runDialog, 0); }}
                                confirmText={edited ? "Save my text" : "Accept"} confirmDisabled={saving || !text.trim()} cancelText="Close" fullWidth maxWidth="sm" hideConfirm={!this.props.canEdit}>
                        {err}
                        <Typography variant="caption" color="text.secondary">Current</Typography>
                        <Typography sx={{ whiteSpace: "pre-wrap", mb: 1 }}>{base}</Typography>
                        <TextField label={`Proposed (${r.kind}) — edit it if you want something else`} value={text} onChange={e => set({ text: e.target.value })} size="small" fullWidth multiline minRows={1} maxRows={8} sx={{ my: 1 }} disabled={!this.props.canEdit} />
                        <Typography variant="body2" color="text.secondary">{r.reason}</Typography>
                        {this.props.canEdit && this.renderAiCheck(dialog, dialog.tag.path, text, set)}
                        {(r.context || r.related?.length > 0) && <Box sx={{ mt: 1, p: 1, bgcolor: "action.hover", borderRadius: 1 }}>
                            {r.context && <Typography variant="body2">{r.context}</Typography>}
                            {(r.related ?? []).map(x => <Typography key={x.path} variant="caption" sx={{ display: "block" }}><span style={mono}>{x.path}</span>: {x.text}</Typography>)}
                        </Box>}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>{r.model} · accepting writes the base text (human specialized) and marks every translation outdated.</Typography>
                        {this.props.canEdit && <Button size="small" color="warning" sx={{ mt: 1 }} onClick={() => { set({ accept: false }); setTimeout(this.runDialog, 0); }}>Reject</Button>}
                    </FormDialog>);
            }
            case "reviewEdit": return (
                <FormDialog {...props} title={`Your text for “${dialog.path}”`} confirmText="Save my text" confirmDisabled={saving || !(dialog.text ?? "").trim()}>
                    {err}
                    <Typography variant="caption" color="text.secondary">Current</Typography>
                    <Typography sx={{ whiteSpace: "pre-wrap", mb: 1 }}>{dialog.current}</Typography>
                    <TextField label={`${project.baseLanguage} text, or an instruction for the AI`} value={dialog.text ?? ""} onChange={e => set({ text: e.target.value })} size="small" fullWidth multiline autoFocus minRows={1} maxRows={8} sx={{ my: 1 }} />
                    <Typography variant="body2" color="text.secondary">Proposal: {dialog.proposed} — {dialog.reason}</Typography>
                    {this.renderAiCheck(dialog, dialog.path, dialog.text ?? "", set)}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>Saved as the base text (human specialized); every translation of the tag is marked outdated and the proposal is cleared.</Typography>
                </FormDialog>);
            default: return null;
        }
    }

    // The context of a proposal behind an (i): the AI's note (what it is, where it shows), the folder's description,
    // the author's note and the related texts - light tooltip so it reads like the table
    contextInfo(path, folder, note, review) {
        const description = this.props.tree.folders.find(f => f.path === folder)?.description;
        const related = review?.related ?? [];
        const has = !!review?.context || related.length > 0;
        const body = (
            <Box sx={{ maxWidth: 460, p: 0.5 }}>
                {review?.context ? <Typography variant="body2" sx={{ mb: 0.5 }}>{review.context}</Typography>
                    : <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>No context note yet: "Add context" asks the AI.</Typography>}
                {description && <Typography variant="caption" sx={{ display: "block" }}><b>Folder:</b> {description}</Typography>}
                {note && <Typography variant="caption" sx={{ display: "block" }}><b>Note:</b> {note}</Typography>}
                {related.length > 0 && <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Related texts</Typography>
                    {related.map(x => <Typography key={x.path} variant="caption" sx={{ display: "block", whiteSpace: "pre-wrap" }}><span style={mono}>{x.path}</span>: {x.text}</Typography>)}
                </Box>}
            </Box>
        );
        return (
            <Tooltip title={body} arrow placement="right" enterDelay={150} leaveDelay={150}
                     componentsProps={{ tooltip: { sx: { bgcolor: "background.paper", color: "text.primary", boxShadow: 4, border: 1, borderColor: "divider", maxWidth: 520 } }, arrow: { sx: { color: "background.paper" } } }}>
                <IconButton size="small" sx={{ p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: 16, color: has ? "info.main" : "text.disabled" }} /></IconButton>
            </Tooltip>
        );
    }

    addContext = () => {
        this.setState({ busy: "*" });
        this.props.loc("Adding context", ["review", "describe"], { silent: true, retry: false, timeout: 90000 })
            .then(r => {
                if (typeof r === "string") { enqueueSnackbar(r, { variant: "error" }); return; }
                enqueueSnackbar(r.status === "done" ? `Context added: ${r.summary}` : `Context job ${r.status} (${r.unitsTotal} batches) - the Jobs tab follows it, the notes appear as they land`, { variant: "info" });
                this.props.onJobsChanged();
                if (r.status === "done") this.loadReviews();
            })
            .catch(e => enqueueSnackbar(e.message, { variant: "error" }))
            .finally(() => this.setState({ busy: null }));
    }

    renderPending() {
        const { project, canEdit } = this.props;
        const { reviews, busy } = this.state;
        const items = reviews?.items ?? [];
        const fixes = items.filter(i => i.review?.kind === "fix");
        const withoutContext = items.filter(i => !i.review?.context).length;
        // the texts get the room: a narrow tag column that breaks at every '/', wide current/proposed columns, the
        // reason in a fixed strip and the three actions stacked
        const text = { whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 14, lineHeight: 1.45 };
        const columns = [
            { id: "path", label: "Tag", width: "14%", sx: { verticalAlign: "top", pr: 0.5 }, render: i => (
                <Box>
                    <span style={{ ...mono, wordBreak: "break-all" }}>{i.path.replace(/\//g, "/\u200B")}</span>
                    {this.contextInfo(i.path, i.folder, i.note, i.review)}
                </Box>
            )},
            { id: "current", label: `Current (${project.baseLanguage})`, width: "27%", sx: { verticalAlign: "top" }, render: i => <Typography sx={text}>{i.text}</Typography> },
            { id: "proposed", label: "Proposed", width: "27%", sx: { verticalAlign: "top" }, render: i => <Typography sx={{ ...text, fontWeight: 500 }}>{i.review?.text}</Typography> },
            { id: "why", label: "Why", width: "20%", sx: { verticalAlign: "top" }, render: i => (
                <Box>
                    <Chip size="small" color={i.review?.kind === "fix" ? "secondary" : "default"} label={i.review?.kind} sx={{ height: 18, fontSize: 10, mb: 0.5 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{i.review?.reason}</Typography>
                </Box>
            )},
        ];
        if (canEdit) columns.push({ id: "act", label: "", width: "12%", sx: { verticalAlign: "top", pl: 0.5 }, render: i => (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Button size="small" variant="contained" disabled={!!busy} onClick={() => this.applyReview(true, [i.path])}>Accept</Button>
                <Button size="small" variant="outlined" disabled={!!busy} onClick={() => this.setState({ dialog: { kind: "reviewEdit", path: i.path, current: i.text, proposed: i.review?.text, reason: i.review?.reason, text: i.review?.text ?? "" } })}>Edit</Button>
                <Button size="small" color="warning" disabled={!!busy} onClick={() => this.applyReview(false, [i.path])}>Reject</Button>
            </Box>
        )});
        return (
            <Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 1 }}>
                    <Typography variant="h6">Pending suggestions</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Accepting writes the {project.baseLanguage} text (human specialized) and marks the tag's translations outdated; the next context run redoes them.</Typography>
                    {canEdit && withoutContext > 0 && <Button size="small" disabled={!!busy} onClick={this.addContext}>Add context ({withoutContext})</Button>}
                    {canEdit && fixes.length > 0 && <Button size="small" disabled={!!busy} onClick={() => this.setState({ dialog: { kind: "bulk", accept: true, paths: fixes.map(f => f.path), label: `Accept all ${fixes.length} fixes` } })}>Accept all fixes ({fixes.length})</Button>}
                    {canEdit && items.length > 0 && <Button size="small" color="warning" disabled={!!busy} onClick={() => this.setState({ dialog: { kind: "bulk", accept: false, all: true, label: `Reject all ${items.length} proposals` } })}>Reject all</Button>}
                </Box>
                {items.length === 0 ? <Typography color="text.secondary">Nothing pending. Coverage → "Review English" asks the AI for a new pass.</Typography>
                    : <PagedTable mode="client" pageSize={50} columns={columns} rows={items} getRowKey={i => i.path} sx={FIXED_TABLE} />}
            </Box>
        );
    }

    render() {
        const { project, tree, canEdit } = this.props;
        const { folder, tags, loadingTags, langs, menu, editing, editText, saving, reviews, pendingMode } = this.state;
        const pendingCount = reviews?.count ?? 0, pendingFixes = (reviews?.items ?? []).filter(i => i.review?.kind === "fix").length;
        const node = tree.folders.find(f => f.path === folder);
        const codes = project.languages.map(l => l.code).filter(c => c !== project.baseLanguage);
        const shown = [project.baseLanguage, ...codes.filter(c => langs.includes(c))];
        const columns = [
            { id: "name", label: "Tag", width: "14%", sx: { verticalAlign: "top" }, render: t => (
                <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Tooltip title={t.path}><span style={mono}>{t.name}</span></Tooltip>
                        {canEdit && <IconButton size="small" onClick={e => this.setState({ menu: { anchor: e.currentTarget, tag: t } })}><MoreVertIcon sx={{ fontSize: 16 }} /></IconButton>}
                    </Box>
                    {t.review && <Chip size="small" color="secondary" label={t.review.kind === "fix" ? "fix suggested" : "suggestion"} sx={{ height: 20, fontSize: 10, cursor: "pointer" }} onClick={() => this.setState({ dialog: { kind: "review", tag: t } })} />}
                </Box>
            )},
            { id: "note", label: "Note", width: "14%", sx: { verticalAlign: "top" }, render: t => editing && editing.path === t.path && editing.lang === "$note" ? (
                <Box sx={{ minWidth: 160 }}>
                    <TextField value={editText} onChange={e => this.setState({ editText: e.target.value })} onKeyDown={this.onEditKey} size="small" fullWidth multiline autoFocus />
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}><Button size="small" onClick={this.cancelEdit} disabled={saving}>Cancel</Button><Button size="small" variant="contained" onClick={this.saveEdit} disabled={saving}>Save</Button></Box>
                </Box>
            ) : <Typography variant="caption" color={t.note ? "text.secondary" : "text.disabled"} onClick={() => this.startNote(t)} sx={{ cursor: canEdit ? "text" : "default", whiteSpace: "pre-wrap" }}>{t.note || "—"}</Typography> },
            ...shown.map(lang => ({ id: lang, label: lang === project.baseLanguage ? `${lang} (base)` : lang, width: `${(72 / shown.length).toFixed(2)}%`, sx: { verticalAlign: "top" }, render: t => this.renderCell(t, lang) })),
        ];
        return (
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                {this.renderTree()}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {(pendingCount > 0 || pendingMode) && (
                        <Alert severity={pendingCount > 0 ? "warning" : "success"} sx={{ mb: 1, py: 0, alignItems: "center" }}
                               action={<Button color="inherit" size="small" onClick={() => this.setState({ pendingMode: !pendingMode })}>{pendingMode ? "Back to the folders" : "Show them"}</Button>}>
                            {pendingCount > 0
                                ? `${pendingCount} suggestion${pendingCount === 1 ? "" : "s"} pending from the English review (${pendingFixes} fix${pendingFixes === 1 ? "" : "es"}, ${pendingCount - pendingFixes} wording). Nothing changes until you accept them.`
                                : "No suggestions pending."}
                        </Alert>
                    )}
                    {pendingMode ? this.renderPending() : <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                        <Typography variant="h6" sx={mono}>{folder ?? "—"}</Typography>
                        {node?.bundle === "boot" && <Chip size="small" color="success" label="shipped" />}
                        {node?.bundle === "onDemand" && <Tooltip title={node?.owner ? `${serviceTitle(node.owner)} owns this folder: its tags are created and deleted there, the texts are edited here or there` : "in no published file"}><Chip size="small" color="info" label={node?.owner ? `${serviceTitle(node.owner)} · on demand` : "on demand"} /></Tooltip>}
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{node?.description || (canEdit ? "no description yet — the AI reads it as context" : "")}</Typography>
                        {canEdit && folder && <>
                            {!node?.owner && <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => this.setState({ dialog: { kind: "create", path: folder + "/", autoTranslate: true } })}>Tag</Button>}
                            <Button size="small" onClick={() => this.setState({ dialog: { kind: "folderEdit", description: node?.description ?? "", bundle: node?.bundle ?? "default", owner: node?.owner } })}>Edit folder</Button>
                            {!node?.owner && <Button size="small" onClick={() => this.setState({ dialog: { kind: "rename", newPath: folder } })}>Rename</Button>}
                            {!node?.owner && <Button size="small" color="error" onClick={() => this.setState({ dialog: { kind: "folderDelete" } })}>Delete</Button>}
                        </>}
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1, alignItems: "center" }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Languages:</Typography>
                        <Chip size="small" label={project.baseLanguage} color="primary" />
                        {codes.map(c => <Chip key={c} size="small" label={c} variant={langs.includes(c) ? "filled" : "outlined"} color={langs.includes(c) ? "primary" : "default"} onClick={() => this.toggleLang(c)} />)}
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" color="text.secondary">{tags.length} tag{tags.length === 1 ? "" : "s"}{loadingTags ? " · loading…" : ""} · click a cell to edit, Ctrl+Enter saves · tiers: {TIERS.slice(1).map(t => `${t.short} ${t.label}`).join(", ")}</Typography>
                    </Box>
                    <PagedTable key={folder} mode="client" pageSize={50} columns={columns} rows={tags} getRowKey={t => t.path} sx={FIXED_TABLE} />
                    </>}
                </Box>
                <Menu open={!!menu} anchorEl={menu?.anchor} onClose={() => this.setState({ menu: null })}>
                    <MenuItem onClick={() => { const t = menu.tag; this.setState({ menu: null }); this.translateNow(t); }}>Translate now (AI, every language)</MenuItem>
                    {!node?.owner && <MenuItem onClick={() => { const t = menu.tag; this.setState({ menu: null, dialog: { kind: "move", tag: t, newPath: t.path } }); }}>Move…</MenuItem>}
                    {!node?.owner && <MenuItem onClick={() => { const t = menu.tag; this.setState({ menu: null, dialog: { kind: "tagDelete", tag: t } }); }}>Delete…</MenuItem>}
                </Menu>
                {this.renderDialog()}
            </Box>
        );
    }
}
// usd4 is used by the job snackbars of other tabs; referenced here to keep the shared helper in one place
void usd4;
